import { useState, useMemo, useCallback } from 'react';

export type SortKey = 'name' | 'created' | 'updated' | 'lastExecuted' | 'successRate';
export type SortDirection = 'asc' | 'desc';

interface Workflow {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  phases?: any[];
  [key: string]: any;
}

interface WorkflowMetrics {
  lastExecuted?: string;
  successRate?: number;
  totalExecutions?: number;
  [key: string]: any;
}

interface UseWorkflowSortFilterOptions {
  workflows: Workflow[];
  metrics: Record<string, WorkflowMetrics>;
  initialSortKey?: SortKey;
  initialSortDir?: SortDirection;
  initialViewMode?: 'grid' | 'table';
}

interface UseWorkflowSortFilterReturn {
  // Data
  sortedWorkflows: Workflow[];
  resultCount: number;
  
  // Sort state
  sortKey: SortKey;
  sortDir: SortDirection;
  setSortKey: (key: SortKey) => void;
  toggleSortDir: () => void;
  
  // Filter state
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  debouncedSearchQuery: string;
  
  // View state
  viewMode: 'grid' | 'table';
  setViewMode: (mode: 'grid' | 'table') => void;
  
  // Utilities
  hasActiveFilters: boolean;
  clearFilters: () => void;
}

export function useWorkflowSortFilter({
  workflows,
  metrics,
  initialSortKey = 'updated',
  initialSortDir = 'desc',
  initialViewMode = 'grid'
}: UseWorkflowSortFilterOptions): UseWorkflowSortFilterReturn {
  // Sort state
  const [sortKey, setSortKey] = useState<SortKey>(initialSortKey);
  const [sortDir, setSortDir] = useState<SortDirection>(initialSortDir);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  
  // View state
  const [viewMode, setViewMode] = useState<'grid' | 'table'>(initialViewMode);
  
  // Debounce search query
  useMemo(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  
  // Filter workflows based on search query
  const filteredWorkflows = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return workflows;
    
    const lowerQuery = debouncedSearchQuery.toLowerCase();
    return workflows.filter(workflow => {
      const searchableText = [
        workflow.name,
        workflow.description,
        ...(workflow.phases?.map((p: any) => p.label) || [])
      ].join(' ').toLowerCase();
      
      return searchableText.includes(lowerQuery);
    });
  }, [workflows, debouncedSearchQuery]);
  
  // Sort workflows
  const sortedWorkflows = useMemo(() => {
    const sorted = [...filteredWorkflows];
    
    sorted.sort((a, b) => {
      let comparison = 0;
      
      switch (sortKey) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
          
        case 'created':
          comparison = new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
          break;
          
        case 'updated':
          comparison = new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime();
          break;
          
        case 'lastExecuted': {
          const getLastExec = (w: Workflow) => {
            const m = metrics[w.id];
            return m?.lastExecuted ? new Date(m.lastExecuted).getTime() : 0;
          };
          comparison = getLastExec(b) - getLastExec(a);
          break;
        }
          
        case 'successRate': {
          const getRate = (w: Workflow) => {
            const m = metrics[w.id];
            return m?.successRate ?? -1;
          };
          comparison = getRate(b) - getRate(a);
          break;
        }
      }
      
      // Apply direction
      return sortDir === 'asc' ? -comparison : comparison;
    });
    
    return sorted;
  }, [filteredWorkflows, sortKey, sortDir, metrics]);
  
  // Toggle sort direction
  const toggleSortDir = useCallback(() => {
    setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
  }, []);
  
  // Check if filters are active
  const hasActiveFilters = debouncedSearchQuery.trim().length > 0;
  
  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
  }, []);
  
  return {
    sortedWorkflows,
    resultCount: sortedWorkflows.length,
    sortKey,
    sortDir,
    setSortKey,
    toggleSortDir,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    viewMode,
    setViewMode,
    hasActiveFilters,
    clearFilters
  };
}
